import { getSupportedAssets } from '@funkit/api-base'
import { resolveTokenTransferConfig } from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import type { Address } from 'viem'

import type { WithdrawalToken } from '../flow/withdrawalFlowConfig'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { useDynamicConfig } from '../statsig/useDynamicConfig'

export const WITHDRAWAL_DESTINATION_TOKENS_QUERY_KEY = 'withdrawalDestinations'

export interface DestinationChain {
  chainId: number
  /** Symbols receivable on this chain, priority ones first. */
  symbols: string[]
}

export interface UseWithdrawalDestinationTokensResult {
  chains: DestinationChain[]
  /** Where the funds land. `undefined` until the curated list resolves. */
  destinationToken: WithdrawalToken | undefined
  selectDestinationToken: (chainId: number, symbol: string) => void
  isLoading: boolean
}

/**
 * Everything a withdrawal can be received as: the curated Statsig
 * `withdrawalconfig` chains and assets, minus
 * `disablewithdrawalchainsandassets`, resolved against api-base's asset
 * metadata — the same pipeline the deposit list uses, and the same one web
 * feeds into its receive-token dropdown.
 *
 * The destination token is the point of this rail. Sending the source token back out
 * on its own chain is a plain transfer with nothing to quote; the relay quote
 * exists precisely so a user holding USDG on one chain can receive USDC on
 * another.
 */
export function useWithdrawalDestinationTokens({
  sourceToken,
  defaultReceiveToken,
}: {
  sourceToken: WithdrawalToken
  /** Symbol to preselect; falls back to the source token's symbol. */
  defaultReceiveToken?: string
}): UseWithdrawalDestinationTokensResult {
  const { apiKey } = useFunkitIdentity()
  const withdrawalConfig = useDynamicConfig('withdrawalconfig')
  const disabledChainAssets = useDynamicConfig(
    'disablewithdrawalchainsandassets',
  )

  const { data: supportedAssets, isLoading } = useQuery({
    queryKey: [WITHDRAWAL_DESTINATION_TOKENS_QUERY_KEY, apiKey],
    queryFn: () => getSupportedAssets({ apiKey, logger }),
    enabled: !!apiKey,
    staleTime: Number.POSITIVE_INFINITY,
  })

  const resolved = useMemo(() => {
    if (!supportedAssets) {
      return undefined
    }
    return resolveTokenTransferConfig({
      chainConfigs: withdrawalConfig.chains ?? [],
      priorityTokens: withdrawalConfig.priorityTokens ?? [],
      disabledChainAssets: disabledChainAssets ?? {},
      supportedAssets,
      logger,
    })
  }, [supportedAssets, withdrawalConfig, disabledChainAssets])

  const chains = useMemo<DestinationChain[]>(() => {
    if (!resolved) {
      return []
    }
    return resolved.chainIdSortOrder.map((chainId) => ({
      chainId,
      symbols: resolved.symbolsByChainId[chainId] ?? [],
    }))
  }, [resolved])

  const [picked, setPicked] = useState<{ chainId: number; symbol: string }>()

  const destinationToken = useMemo<WithdrawalToken | undefined>(() => {
    if (!resolved) {
      return undefined
    }

    // The user's pick, else the host's preferred receive token, else the source
    // symbol — each only if the curated list actually offers it.
    const candidates = picked
      ? [picked]
      : [
          ...(defaultReceiveToken
            ? chains.map((c) => ({
                chainId: c.chainId,
                symbol: defaultReceiveToken,
              }))
            : []),
          ...chains.map((c) => ({
            chainId: c.chainId,
            symbol: sourceToken.symbol,
          })),
        ]

    for (const candidate of candidates) {
      const address =
        resolved.addressBySymbolByChainId[candidate.chainId]?.[candidate.symbol]
      if (address) {
        return {
          chainId: String(candidate.chainId),
          address: address as Address,
          symbol: candidate.symbol,
        }
      }
    }
    return undefined
  }, [resolved, picked, chains, defaultReceiveToken, sourceToken.symbol])

  return {
    chains,
    destinationToken,
    selectDestinationToken: (chainId, symbol) => setPicked({ chainId, symbol }),
    isLoading,
  }
}
